import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { getNarrationAudioUrl } from '../firebase/storage';

interface AudioContextType {
  isMuted: boolean;
  toggleMute: () => void;
  playAudioForSlide: (slideId: string) => Promise<void>;
  stopAllAudio: () => void;
  isPlaying: boolean;
  togglePlayback: () => void;
  userPaused: boolean;
  currentAudioId: string | null;
  isLoading: boolean;
}

const AudioContext = createContext<AudioContextType>({
  isMuted: false,
  toggleMute: () => {},
  playAudioForSlide: async () => {},
  stopAllAudio: () => {},
  isPlaying: false,
  togglePlayback: () => {},
  userPaused: false,
  currentAudioId: null,
  isLoading: false,
});

export const useAudio = () => useContext(AudioContext);

interface AudioProviderProps {
  children: React.ReactNode;
}

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [currentAudioId, setCurrentAudioId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentSlideIdRef = useRef<string | null>(null);
  const isTransitioningRef = useRef<boolean>(false);
  const playbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stopAllAudio = useCallback(() => {
    console.log('[AudioContext] Stopping all audio');
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.removeEventListener('ended', () => {});
      audioRef.current.removeEventListener('error', () => {});
      audioRef.current = null;
    }
    if (playbackTimeoutRef.current) {
      clearTimeout(playbackTimeoutRef.current);
      playbackTimeoutRef.current = null;
    }
    setIsPlaying(false);
    currentSlideIdRef.current = null;
    setCurrentAudioId(null);
    setUserPaused(false);
    isTransitioningRef.current = false;
  }, []);

  // Play audio for a specific slide
  const playAudioForSlide = useCallback(async (slideId: string) => {
    console.log('[AudioContext] Playing audio for slide:', slideId);
    
    try {
      // Stop any existing audio first
      stopAllAudio();
      
      // Get the narration audio URL
      const audioUrl = await getNarrationAudioUrl('slide', slideId);
      if (!audioUrl) {
        console.log('[AudioContext] No audio URL found for slide:', slideId);
        return;
      }
      
      // Create and configure audio element
      const audio = new Audio(audioUrl);
      
      // Store audio element before adding event listeners
      audioRef.current = audio;
      setCurrentAudioId(slideId);
      
      // Set up event listeners
      audio.addEventListener('canplaythrough', () => {
        console.log('[AudioContext] Audio loaded and ready to play');
        setIsLoading(false);
        if (!isMuted && audioRef.current === audio) {
          audio.play().catch(error => {
            console.error('[AudioContext] Error playing audio:', error);
          });
        }
      });
      
      audio.addEventListener('ended', () => {
        console.log('[AudioContext] Audio finished playing');
        if (audioRef.current === audio) {
          setIsPlaying(false);
          audioRef.current = null;
          setCurrentAudioId(null);
        }
      });
      
      audio.addEventListener('error', (error) => {
        console.error('[AudioContext] Audio error:', error);
        if (audioRef.current === audio) {
          setIsPlaying(false);
          setIsLoading(false);
          audioRef.current = null;
          setCurrentAudioId(null);
        }
      });
      
      setIsPlaying(true);
      setIsLoading(true);
      
    } catch (error) {
      console.error('[AudioContext] Error setting up audio:', error);
      setIsPlaying(false);
      setIsLoading(false);
      audioRef.current = null;
      setCurrentAudioId(null);
    }
  }, [stopAllAudio, isMuted]);

  const togglePlayback = useCallback(() => {
    if (!audioRef.current || isTransitioningRef.current) {
      console.log('[AudioContext] Cannot toggle playback - no audio or transition in progress');
      return;
    }

    if (isPlaying) {
      console.log('[AudioContext] Pausing audio');
      audioRef.current.pause();
      setIsPlaying(false);
      setUserPaused(true);
    } else {
      console.log('[AudioContext] Resuming audio');
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setUserPaused(false);
        })
        .catch(error => {
          console.error('[AudioContext] Error toggling playback:', error);
          setIsPlaying(false);
          setUserPaused(true);
        });
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      if (audioRef.current) {
        audioRef.current.muted = !prev;
      }
      return !prev;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllAudio();
    };
  }, [stopAllAudio]);

  const value = {
    isPlaying,
    isMuted,
    userPaused,
    togglePlayback,
    toggleMute,
    playAudioForSlide,
    stopAllAudio,
    currentAudioId,
    isLoading
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};
